/**
 * Cliente HTTP del backend.
 *
 * El backend responde siempre `{detail, code}` ante un error (§5), así que
 * aquí se traduce eso a una excepción con el mensaje ya listo para mostrar.
 * Nunca se inventan mensajes: si el servidor explicó el problema, se usa su
 * explicación.
 */

import type { FiltrosTabla } from './tipos'

export class ErrorApi extends Error {
  readonly code: string
  readonly status: number
  readonly errors?: Record<string, string[]>

  constructor(
    mensaje: string,
    opciones: { code?: string; status?: number; errors?: Record<string, string[]> } = {},
  ) {
    super(mensaje)
    this.name = 'ErrorApi'
    this.code = opciones.code ?? 'error'
    this.status = opciones.status ?? 0
    this.errors = opciones.errors
  }

  /** ¿Reintentar tiene sentido? Un 4xx de validación no mejora reintentando. */
  get esReintentable(): boolean {
    return this.status === 0 || this.status >= 500 || this.status === 429
  }
}

const BASE = '/api'

const METODOS_SEGUROS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE'])

/**
 * Token CSRF que Django deja en una cookie.
 *
 * Hace falta en toda petición que cambie algo: la sesión viaja en cookie, y
 * sin este encabezado Django rechaza la escritura con un 403. La cookie no
 * es `HttpOnly` justamente para poder leerla desde aquí.
 */
function csrf(): string {
  const cookie = document.cookie.split('; ').find((c) => c.startsWith('csrftoken='))
  return cookie ? decodeURIComponent(cookie.slice('csrftoken='.length)) : ''
}

async function peticion<T>(ruta: string, init: RequestInit = {}): Promise<T> {
  const metodo = (init.method ?? 'GET').toUpperCase()
  let respuesta: Response
  try {
    respuesta = await fetch(`${BASE}${ruta}`, {
      ...init,
      // La sesión va en cookie; sin esto, una petición a otro origen no la
      // llevaría.
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        ...(METODOS_SEGUROS.has(metodo) ? {} : { 'X-CSRFToken': csrf() }),
        ...init.headers,
      },
    })
  } catch {
    throw new ErrorApi(
      'No se pudo contactar al servidor. Comprueba que el backend esté corriendo en :8000.',
      { code: 'sin_conexion' },
    )
  }

  if (respuesta.status === 204) return undefined as T

  const texto = await respuesta.text()
  let cuerpo: unknown
  try {
    cuerpo = texto ? JSON.parse(texto) : null
  } catch {
    cuerpo = null
  }

  if (!respuesta.ok) {
    const datos = (cuerpo ?? {}) as {
      detail?: string
      code?: string
      errors?: Record<string, string[]>
    }
    throw new ErrorApi(datos.detail || `El servidor respondió ${respuesta.status}.`, {
      code: datos.code,
      status: respuesta.status,
      errors: datos.errors,
    })
  }

  return cuerpo as T
}

/** Convierte los filtros en query string, expandiendo los valores múltiples. */
export function comoQuery(params: Record<string, unknown> = {}): string {
  const query = new URLSearchParams()
  for (const [clave, valor] of Object.entries(params)) {
    if (valor === undefined || valor === null || valor === '') continue
    if (Array.isArray(valor)) {
      // django-filter espera el parámetro repetido, no una lista separada por comas.
      for (const v of valor) query.append(clave, String(v))
    } else if (typeof valor === 'boolean') {
      if (valor) query.set(clave, 'true')
    } else {
      query.set(clave, String(valor))
    }
  }
  const texto = query.toString()
  return texto ? `?${texto}` : ''
}

export const api = {
  get: <T>(ruta: string) => peticion<T>(ruta),
  post: <T>(ruta: string, cuerpo?: unknown) =>
    peticion<T>(ruta, { method: 'POST', body: JSON.stringify(cuerpo ?? {}) }),
  patch: <T>(ruta: string, cuerpo: unknown) =>
    peticion<T>(ruta, { method: 'PATCH', body: JSON.stringify(cuerpo) }),
  delete: <T>(ruta: string) => peticion<T>(ruta, { method: 'DELETE' }),

  /** URL absoluta de una descarga, para usar en un `<a href>`. */
  urlDescarga: (ruta: string) => `${BASE}${ruta}`,
}

export function filtrosAQuery(filtros: FiltrosTabla): string {
  return comoQuery(filtros as Record<string, unknown>)
}
